import React, { useState, useEffect } from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { Position } from "../../components/BuildingBlocks/Position";
import { CRMRadioTab } from "../../components/CRMRadioTab/CRMRadioTab";
import { CRMButtonIcon } from "../../components/CRMButtonIcon/CRMButtonIcon";
import { CRMFormButtonIconWithLabel } from "../../components/CRMFormButtonIconWithLabel/CRMFormButtonIconWithLabel";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMCardInside } from "../../components/CRMCardInside/CRMCardInside";
import { CRMSpacer } from "../../components/CRMSpacer/CRMSpacer";
import { TextCenter } from "../../components/TextCenter/TextCenter";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import CRMInputLabelAndErrorWrapComponent from "../../components/CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import CRMInputGeneralComponent from "../../components/CRMInputs/CRMInputGeneralComponent/CRMInputGeneralComponent";
import { CRMInputPhoneNumbers } from "../../components/CRMInputPhoneNumbers/CRMInputPhoneNumbers";
import { CRMSpacing } from "../../models/CRMSpacing";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { castNewFormStatusToLegacy } from "../../util";
import { doesErrorKeyExist } from "../../../../shared/src/codecs/errors";
import { editFormEditableValue, TUnpackEditable } from "../../../../shared/src/codecs/types/formEditable";
import { TListingSellerSuggestedSimilarUserForm, TListingSellerUserEditForm } from "../../../../domain/codecs/form/ListingSellerUserEditForm";
import { CRMClientSuggestion } from "../../components/CRMClientSuggestion/CRMClientSuggestion";
import { CRMZIndex } from "../../models/CRMZIndex";
import { array, ord } from "fp-ts";
import { CRMDropdownComponent } from "../../components/CRMDropdownComponent/CRMDropdownComponent";
import { enumToIDropdownOptions } from "../../functions/enumToIDropdownOptions";
import { BuyerPartyUserTypes, SellerPartyUserTypes, TUserCaseUserType, UserCaseUserType_UserCaseUserTypeDisplayName } from "../../../../domain/codecs/UserCaseUserType";
import { CRMPartyGroupHeading } from "../../components/CRMPartyGroupHeading/CRMPartyGroupHeading";
import { groupMembersByPartyRole } from "../../models/CRMPartyRoleGrouping";
import { Ord } from "fp-ts/lib/boolean";
import { pipe } from "fp-ts/lib/function";

type TSuggestionInput = "first_name" | "last_name" | "email" | "none";

export const CRMPartyMembersFormContainer = (props: TContainerStateProps): JSX.Element => {
    const [selectedMemberIndex, setSelectedMemberIndex] = useState(0);
    const [inputShowingSuggestions, setInputShowingSuggestions] = useState<TSuggestionInput>("none");
    const [suggestionInputInFocus, setSuggestionInputInFocus] = useState<TSuggestionInput>("none");

    const onSwapMember = (form: TListingSellerSuggestedSimilarUserForm) => props.dispatch({
        type: "PARTY_PAGE_SWAP_MEMBER",
        payload: form,
    });

    const members = (): Array<TListingSellerUserEditForm> => props.state.forms.party_page.data.output.members;

    const userTypeOptions = (): Array<TUserCaseUserType> =>
        props.state.forms.party_page.data.output.read_only.is_seller_party
            ? SellerPartyUserTypes
            : BuyerPartyUserTypes;
    const getSelectedMember = (): TListingSellerUserEditForm => members()[selectedMemberIndex];

    const updateMemberValue = <K extends keyof TUnpackEditable<TListingSellerUserEditForm>>(key: K, value: TListingSellerUserEditForm["edited"][K]) => props.dispatch({
        type: "PARTY_PAGE_UPDATE_MEMBER",
        payload: members().map((member, index) => index === selectedMemberIndex ? { ...editFormEditableValue(member, key, value) } : member),
    });

    const updatePartyMemberRole = (user_type: TUserCaseUserType) => props.dispatch({
        type: "PARTY_PAGE_UPDATE_MEMBER",
        payload: members().map((member, index): TListingSellerUserEditForm =>
            index === selectedMemberIndex
                ? {
                    ...member,
                    status: "requiresSubmission",
                    children: { ...member.children, user_type },
                }
                : member
        ),
    });

    useEffect(
        () => {
            if (selectedMemberIndex > members().length - 1) {
                setSelectedMemberIndex(selectedMemberIndex - 1 < 0 ? 0 : selectedMemberIndex - 1);
            }
        },
        [members().length],
    );

    return (
        <div
            onMouseLeave={() => setTimeout(() => setInputShowingSuggestions("none"), 1000)}
            onMouseEnter={() => {
                if (inputShowingSuggestions === "none" && suggestionInputInFocus !== "none") {
                    setInputShowingSuggestions(suggestionInputInFocus);
                }
            }}
        >
            <CRMCardInside>
                <CRMPadding size="medium">
                    <CRMParagraph>
                        NB: editing the client edits them on all parties.
                    </CRMParagraph>
                </CRMPadding>
            </CRMCardInside>
            <CRMSpacer size="medium" />
            <SpacingRow spacing={CRMSpacing.LARGE} alignItems="flex-start" childSize="2fr 1fr">
                <Position position="relative">
                    {members().length === 0 &&
                        <CRMPadding size="tiny">
                            <TextCenter>
                                <CRMParagraph>
                                    Add <WeightBold>a new person</WeightBold> to get started.
                                </CRMParagraph>
                            </TextCenter>
                        </CRMPadding>
                    }

                    {members().length > 0 && selectedMemberIndex <= members().length - 1 &&
                        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                            <Position position="relative">
                                <CRMInputLabelAndErrorWrapComponent label="First name">
                                    <CRMInputGeneralComponent
                                        inputType="text"
                                        displayError={doesErrorKeyExist("edited.first_name", getSelectedMember().validationErrors)}
                                        placeholder="John"
                                        value={getSelectedMember().edited.first_name}
                                        onChange={(value) => updateMemberValue("first_name", value)}
                                        onFocus={() => { setInputShowingSuggestions("first_name"); setSuggestionInputInFocus("first_name"); }}
                                        onBlur={() => setSuggestionInputInFocus("none")}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>
                                {inputShowingSuggestions === "first_name" &&
                                    <CRMPartyMemberFormSuggestions member={getSelectedMember()} onSwapMember={onSwapMember} />
                                }
                            </Position>

                            <Position position="relative">
                                <CRMInputLabelAndErrorWrapComponent label="Last name">
                                    <CRMInputGeneralComponent
                                        inputType="text"
                                        displayError={doesErrorKeyExist("edited.last_name", getSelectedMember().validationErrors)}
                                        placeholder="Doe"
                                        value={getSelectedMember().edited.last_name}
                                        onChange={(value) => updateMemberValue("last_name", value)}
                                        onFocus={() => { setInputShowingSuggestions("last_name"); setSuggestionInputInFocus("last_name"); }}
                                        onBlur={() => setSuggestionInputInFocus("none")}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>
                                {inputShowingSuggestions === "last_name" &&
                                    <CRMPartyMemberFormSuggestions member={getSelectedMember()} onSwapMember={onSwapMember} />
                                }
                            </Position>

                            <Position position="relative">
                                <CRMInputLabelAndErrorWrapComponent label="Email">
                                    <CRMInputGeneralComponent
                                        inputType="email"
                                        displayError={doesErrorKeyExist("edited.email", getSelectedMember().validationErrors)}
                                        placeholder="john@doe.com"
                                        value={getSelectedMember().edited.email || ""}
                                        onChange={(value) => updateMemberValue("email", value || null)}
                                        onFocus={() => { setInputShowingSuggestions("email"); setSuggestionInputInFocus("email"); }}
                                        onBlur={() => setSuggestionInputInFocus("none")}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>
                                {inputShowingSuggestions === "email" &&
                                    <CRMPartyMemberFormSuggestions member={getSelectedMember()} onSwapMember={onSwapMember} />
                                }
                            </Position>

                            <CRMInputLabelAndErrorWrapComponent label="Type">
                                <CRMDropdownComponent
                                    options={enumToIDropdownOptions(
                                        userTypeOptions(),
                                        UserCaseUserType_UserCaseUserTypeDisplayName,
                                    )}
                                    displayError={false}
                                    value={getSelectedMember().children.user_type}
                                    onChange={updatePartyMemberRole}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            <CRMInputLabelAndErrorWrapComponent label="Description of party member">
                                <CRMInputGeneralComponent
                                    inputType="text"
                                    displayError={doesErrorKeyExist("edited.description_of_user", getSelectedMember().validationErrors)}
                                    value={getSelectedMember().edited.description_of_user}
                                    onChange={(value) => updateMemberValue("description_of_user", value)}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            <CRMInputLabelAndErrorWrapComponent label="Phone numbers">
                                <CRMInputPhoneNumbers
                                    placeholder="Add phone number"
                                    phoneNumbers={getSelectedMember().children.phone_numbers}
                                    createPhoneNumberStatus={
                                        props.state.forms.party_page.data.output.member_phone_number_create.status === "untouched"
                                            ? "requiresSubmission"
                                            : props.state.forms.party_page.data.output.member_phone_number_create.status
                                    }
                                    showVerificationStatus={false}
                                    onAddNew={() => props.dispatch({
                                        type: "PARTY_PAGE_ADD_PHONE_NUMBER_TO_MEMBER",
                                        payload: getSelectedMember().original.id,
                                    })}
                                    onRemove={(phoneNumber) => props.dispatch({
                                        type: "PARTY_PAGE_REMOVE_PHONE_NUMBER_FROM_MEMBER",
                                        payload: phoneNumber,
                                    })}
                                    onChange={(phoneNumbers) => props.dispatch({
                                        type: "PARTY_PAGE_UPDATE_MEMBER_PHONE_NUMBER",
                                        payload: phoneNumbers,
                                    })}
                                />
                            </CRMInputLabelAndErrorWrapComponent>
                        </SpacingColumn>
                    }
                </Position>

                <SpacingColumn spacing={CRMSpacing.LARGE}>
                    {/* Grouped for display only — selection still keys off the member's
                        position in the unsorted form array, so re-typing a person does not
                        silently select someone else. */}
                    {groupMembersByPartyRole({
                        members: members().map((member, index) => ({ member, index })),
                        getUserType: ({ member }) => member.children.user_type,
                        getSortName: ({ member }) => `${member.edited.last_name} ${member.edited.first_name}`,
                    }).map((group) => (
                        <SpacingColumn key={group.label} spacing={CRMSpacing.SMALL}>
                            <CRMPartyGroupHeading
                                label={group.label}
                                tone={group.tone}
                                count={group.members.length}
                            />

                            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                {group.members.map(({ member, index }) => (
                                    <CRMRadioTab
                                        key={index}
                                        isSelected={selectedMemberIndex === index}
                                        label={`${member.edited.first_name} ${member.edited.last_name.charAt(0)}${member.edited.last_name !== "" ? "." : ""}`}
                                        onClick={() => setSelectedMemberIndex(index)}
                                        options={[
                                            {
                                                label: "Remove from party",
                                                onClick: () => props.dispatch({
                                                    type: "PARTY_PAGE_DELETE_MEMBER",
                                                    payload: member.children.delete_form,
                                                }),
                                            },
                                        ]}
                                    />
                                ))}
                            </SpacingColumn>
                        </SpacingColumn>
                    ))}
                    <CRMFormButtonIconWithLabel
                        formStatus={
                            castNewFormStatusToLegacy(props.state.forms.party_page.data.output.member_create.status) === "untouched"
                                ? "requiresSubmission"
                                : castNewFormStatusToLegacy(props.state.forms.party_page.data.output.member_create.status)
                        }
                        ButtonElement={(buttonProps) => <CRMButtonIcon variant="primary" {...buttonProps} />}
                        icon="person-add"
                        label="Add new person"
                        onClick={() => props.dispatch({ type: "PARTY_PAGE_ADD_MEMBER" })}
                    />
                </SpacingColumn>
            </SpacingRow>
        </div>
    );
};

const CRMPartyMemberFormSuggestions = (props: {
    member: TListingSellerUserEditForm,
    onSwapMember: (form: TListingSellerSuggestedSimilarUserForm) => void,
}): JSX.Element =>
    props.member.children.suggested_similar_users.length > 0
        ? <div style={{ position: "absolute", top: "60px", zIndex: CRMZIndex.THIRD_Z, width: "300px" }}>
            <SpacingColumn spacing={CRMSpacing.TINY}>
                {pipe(
                    props.member.children.suggested_similar_users,
                    array.sortBy([ord.contramap((s: TListingSellerSuggestedSimilarUserForm) => typeof s.children.email === "string")(Ord)]),
                    array.reverse,
                    array.mapWithIndex((i, form) =>
                        <CRMClientSuggestion
                            context="listing-seller-party"
                            key={i}
                            form={form}
                            onUse={(f) => props.onSwapMember(f as TListingSellerSuggestedSimilarUserForm)}
                        />
                    ),
                )}
            </SpacingColumn>
        </div>
        : <></>;
